// @flow
import * as React from 'react';
import styled from 'styled-components';
import ToggleHelperContainer from 'containers/ToggleHelperContainer';
import { SecondaryButton } from 'components/Button';
import Container from 'components/Container';
import Textarea from 'components/Textarea';
import Box from 'components/Box';
import RenderWithDelay from 'containers/RenderWithDelay';
import Spinner from 'components/Spinner';
import { colors, fonts, richTextEditor, transition } from '../theme';

const MIN_HEIGHT = 135;

const ToolbarPlaceholder = styled.div`
  height: 40px;
  border-radius: 4px;
  background-color: ${colors.offWhite};
  margin-bottom: 7px;
`;

const EditorPlaceholder = styled.div`
  height: ${MIN_HEIGHT}px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: solid 1px ${colors.silver};
  border-radius: 4px;
`;

const InputLoading = () => (
  <>
    <ToolbarPlaceholder />
    <EditorPlaceholder>
      <RenderWithDelay>
        <Spinner size="small" />
      </RenderWithDelay>
    </EditorPlaceholder>
  </>
);

const StyledReactQuill = styled.div`
  .ql-toolbar {
    border-radius: 4px;
    border: none;
    color: ${colors.battleshipGrey};
    background-color: ${colors.offWhite};
    margin-bottom: 7px;
  }
  .ql-container {
    border: none;
  }
  .ql-editor {
    ${props =>
      props.$paddingTop
        ? `
      padding-top: ${props.$paddingTop}px;
    `
        : ''}
    border: solid 1px ${colors.silver};
    border-radius: 4px;
    color: ${colors.charcoalGrey};
    min-height: ${props =>
      props.$paddingTop ? MIN_HEIGHT + props.$paddingTop : MIN_HEIGHT}px;
    max-height: 600px;
    overflow: hidden;
    overflow-y: scroll;
    overflow-x: scroll;
    background-color: ${props =>
      props.disabled ? colors.offWhite : colors.white};
    transition: all ${transition};
    ${props =>
      props.hasError &&
      `
    border-color: ${colors.scarlet};
    box-shadow: 0 0 0 1px ${colors.scarlet};
  `}
    &:focus {
      border-color: ${colors.waterBlue};
      box-shadow: 0 0 0 1px ${colors.waterBlue};
      outline: 0;
    }
    &.ql-blank::before {
      /* placeholder text */
      font-family: ${fonts.sans};
      font-style: normal;
      color: ${colors.coolGrey};
    }

    ${richTextEditor}
  }
`;

const headlineToolbar = [['bold', 'italic', 'underline', 'strike']];
const baseToolbar = [['bold', 'italic', 'underline', 'strike', 'link']];
const simpleToolbar = [
  ['bold', 'italic', 'underline', 'strike', 'link'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['clean']
];

const baseFormats = ['bold', 'italic', 'underline', 'strike'];
const simpleFormats = [...baseFormats, 'link', 'script', 'list'];
const headingFormats = [...baseFormats, 'header'];
const limitedFormats = [...simpleFormats, 'header'];
const allFormats = [...limitedFormats, 'blockquote'];

type Delta = {
  ops: Array<{ insert: string, attributes?: Object }>
};

type Props = {
  name: string,
  value: ?string,
  onChange: string => void,
  onKeyUp?: string => void,
  labelRef?: { current: null | React$ElementRef<'label'> },
  disabled?: boolean,
  enableRawHtml?: boolean,
  hasError?: boolean,
  'data-qa-id'?: string,
  simple?: boolean,
  limited?: boolean,
  h1?: boolean,
  h2?: boolean,
  intro?: string
};

type State = {
  reactQuillComponent: ?React.ComponentType<any>,
  paddingTop: number
};

export default class RichTextInput extends React.PureComponent<Props, State> {
  state = {
    reactQuillComponent: null,
    paddingTop: 0
  };

  stopListening: ?() => void;

  reactQuillRef = React.createRef<Object>();

  setReactQuillRef = (element: ?{ getEditor: Function }) => {
    this.reactQuillRef.current = element;
    if (element) {
      element.getEditor().history.clear();
    }
  };

  containerRef = React.createRef<HTMLElement>();

  introRef = React.createRef<HTMLElement>();

  toolbarOptions = this.props.simple
    ? baseToolbar
    : this.props.limited
    ? [[{ header: [4, 5, false] }], ...simpleToolbar]
    : this.props.h1
    ? [[{ header: [1] }], ...headlineToolbar]
    : this.props.h2
    ? [[{ header: [2] }], ...headlineToolbar]
    : [[{ header: [2, 3, 4, 5, false] }], ...simpleToolbar, ['blockquote']];

  componentDidMount() {
    import(/* webpackChunkName: "ReactQuill" */ 'react-quill').then(object => {
      this.setState({ reactQuillComponent: object.default });

      if (this.containerRef.current) {
        this.applyAccessibilityHacks(this.containerRef.current);
      }

      if (this.introRef.current) {
        const paddingTop = this.introRef.current.offsetHeight + 30;
        this.setState({ paddingTop });
      }
    });

    const label = this.props.labelRef && this.props.labelRef.current;
    if (label) {
      label.addEventListener('click', this.focusInput);
    }
  }

  componentWillUnmount() {
    if (this.stopListening) this.stopListening();
  }

  stopListening = () => {
    const label = this.props.labelRef && this.props.labelRef.current;
    if (label) {
      label.removeEventListener('click', this.focusInput);
    }
  };

  focusInput = () => {
    if (this.reactQuillRef.current) {
      this.reactQuillRef.current.focus();
    }
  };

  /**
   * Applies accessibility to a quill editor
   * TODO: Deprecate this method once this issue is resolved (https://github.com/quilljs/quill/issues/1173)
   * @param {object}		editor		- A Quill editor instance
   */
  applyAccessibilityHacks(editor: Object) {
    // Get ref to the toolbar, its not available through the quill api ughh
    const query = editor.querySelectorAll('.ql-toolbar');
    if (query.length !== 1) {
      // No toolbars found OR multiple which is not what we expect either
      return;
    }

    const toolBar = query[0];

    // apply aria labels to base buttons
    const buttons = toolBar.getElementsByTagName('button');
    for (let i = 0; i < buttons.length; i++) {
      const button = buttons[i];
      const buttonClass = button.getAttribute('class');
      const className = buttonClass ? buttonClass.toLowerCase() : '';

      if (className.indexOf('bold') >= 0) {
        button.setAttribute('aria-label', 'Toggle bold text');
      } else if (className.indexOf('italic') >= 0) {
        button.setAttribute('aria-label', 'Toggle italic text');
      } else if (className.indexOf('link') >= 0) {
        button.setAttribute('aria-label', 'Create link');
      } else if (className.indexOf('strike') >= 0) {
        button.setAttribute('aria-label', 'Toggle strikethrough text');
      } else if (className.indexOf('clean') >= 0) {
        button.setAttribute('aria-label', 'Clear text formatting');
      } else if (className.indexOf('underline') >= 0) {
        button.setAttribute('aria-label', 'Toggle underline text');
      } else if (className.indexOf('blockquote') >= 0) {
        button.setAttribute('aria-label', 'Toggle blockquote text');
      } else if (className.indexOf('list') >= 0 && button.value === 'ordered') {
        button.setAttribute('aria-label', 'Toggle ordered list');
      } else if (className.indexOf('list') >= 0 && button.value === 'bullet') {
        button.setAttribute('aria-label', 'Toggle bulleted list');
      }
    }

    // Make pickers work with keyboard and apply aria labels
    // FIXME: When you open a submenu with the keyboard and close it with the mouse by click somewhere else, the menu aria-hidden value is incorrectly left to `false`
    const pickers = toolBar.getElementsByClassName('ql-picker');
    for (let i = 0; i < pickers.length; i++) {
      const picker = pickers[i];

      const label = picker.getElementsByClassName('ql-picker-label')[0];
      const optionsContainer = picker.getElementsByClassName(
        'ql-picker-options'
      )[0];
      const options = optionsContainer.getElementsByClassName('ql-picker-item');

      label.setAttribute('role', 'button');
      label.setAttribute('aria-haspopup', 'true');
      label.setAttribute('tabindex', '0');

      if (i === 0) {
        // HACK ALERT
        // This is our size select box.. Works for us as we only have the one drop box
        label.setAttribute('aria-label', 'Font Size');
      }

      optionsContainer.setAttribute('aria-hidden', 'true');
      optionsContainer.setAttribute('aria-label', 'submenu');

      for (let x = 0; x < options.length; x++) {
        const item = options[x];

        if (item) {
          item.setAttribute('tabindex', '0');
          item.setAttribute('role', 'button');

          // Read the css 'content' values and generate aria labels
          const size = window
            .getComputedStyle(item, ':before')
            .content.replace('"', '');
          item.setAttribute('aria-label', size);
          item.addEventListener('keyup', e => {
            if (e.keyCode === 13) {
              item.click();
              optionsContainer.setAttribute('aria-hidden', 'true');
            }
          });
        }
      }

      label.addEventListener('keyup', e => {
        if (e.keyCode === 13) {
          label.click();
          optionsContainer.setAttribute('aria-hidden', 'false');
        }
      });
    }
  }

  matchers = [
    [
      Node.ELEMENT_NODE,
      (node: Node, delta: Delta) => {
        const ops = delta.ops
          .filter(op => op.insert && typeof op.insert === 'string')
          .map(op => {
            let newOp;
            const formats = this.props.simple
              ? simpleFormats
              : this.props.limited
              ? limitedFormats
              : this.props.h1 || this.props.h2
              ? headingFormats
              : allFormats;
            if (op.attributes) {
              const attributes = Object.keys(op.attributes).reduce(
                (acc, key) => {
                  const attributeValue = op.attributes
                    ? op.attributes[key]
                    : null;
                  if (formats.includes(key)) {
                    const value =
                      key === 'header' && typeof attributeValue === 'number'
                        ? this.props.h1
                          ? 1
                          : this.props.h2
                          ? 2
                          : Math.max(attributeValue, this.props.limited ? 4 : 2)
                        : attributeValue;
                    return { ...acc, [key]: value };
                  }
                  return acc;
                },
                {}
              );
              newOp = { ...op, attributes };
            } else {
              newOp = { insert: op.insert };
            }
            return newOp;
          });
        delta.ops = ops;
        return delta;
      }
    ]
  ];

  get richTextEditor() {
    const {
      onChange,
      onKeyUp,
      disabled,
      hasError,
      enableRawHtml,
      ...props
    } = this.props;
    const ReactQuill = this.state.reactQuillComponent;

    if (props.value === undefined || props.value === null) {
      props.value = '';
    }

    if (ReactQuill) {
      return (
        <div ref={this.containerRef}>
          <Box position="relative">
            <StyledReactQuill
              disabled={disabled}
              hasError={hasError}
              data-qa-id={props['data-qa-id']}
              $paddingTop={this.state.paddingTop}
            >
              <ReactQuill
                ref={this.setReactQuillRef}
                {...props}
                readOnly={disabled}
                onChange={onChange}
                onKeyUp={onKeyUp}
                style={{
                  backgroundColor: disabled ? '#eceeef' : 'white'
                }}
                modules={{
                  clipboard: {
                    matchVisual: false,
                    matchers: this.matchers
                  },
                  toolbar: this.toolbarOptions
                }}
              />
            </StyledReactQuill>

            {this.props.intro && (
              <Box
                position="absolute"
                top={60}
                left={0}
                width={1}
                px={15}
                color={colors.battleshipGrey}
                pointerEvents="none"
              >
                <Box
                  p="2px 5px"
                  bg={colors.platinum}
                  display="inline-block"
                  fontSize={14}
                >
                  <div ref={this.introRef}>{this.props.intro}</div>
                </Box>
              </Box>
            )}
          </Box>
        </div>
      );
    }
    return <InputLoading />;
  }

  get rawHtmlEditor() {
    const { onChange, labelRef, enableRawHtml, ...props } = this.props;
    return (
      <Textarea
        rows="20"
        {...props}
        onChange={e => {
          this.props.onChange(e.target.value);
        }}
      />
    );
  }

  render() {
    if (!this.props.enableRawHtml) return this.richTextEditor;
    return (
      <ToggleHelperContainer>
        {(rawHtmlMode, toggle) => (
          <>
            <Container pb={2}>
              <SecondaryButton size="small" onClick={toggle}>
                {rawHtmlMode
                  ? getText('Edit as rich text')
                  : getText('Edit as HTML')}
              </SecondaryButton>
            </Container>
            {rawHtmlMode ? this.rawHtmlEditor : this.richTextEditor}
          </>
        )}
      </ToggleHelperContainer>
    );
  }
}
